import React, { useCallback, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Popover, Input, Button, message } from 'antd';
import { StarFilled, StarOutlined, CloseOutlined } from '@ant-design/icons';
import {
	upsertWorkOrderStar,
	removeWorkOrderStar,
} from '../../thunks/work_order_user_stars_thunks';
import { canAddWOLabelsOrStarNotes } from '../../utils/DataAccessUtils';
import useMountEffect from '../../hooks/useMountEffect';
import useExclusivePopover from '../../hooks/useExclusivePopover';
import ProUpsellContent from './ProUpsellContent';
import './StarNotePopover.less';

interface OwnProps {
	workOrderId: number | string;
	initialStarred?: boolean;
	initialNote?: string | null;
	noteDisplay?: React.ReactNode;
	children: React.ReactElement;
}

interface Props extends OwnProps {
	canAddNote: boolean;
	upsertWorkOrderStar: (workOrderId: number | string, starNote: string | null) => Promise<any>;
	removeWorkOrderStar: (workOrderId: number | string) => Promise<any>;
}

const NOTE_MAX_LENGTH = 40;

const StarredToastIcon: React.FC = () => (
	<span className="work-order-starred-feedback-toast__badge box-border inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full border border-solid border-calendulaGold-5 bg-calendulaGold-5">
		<StarFilled className="block leading-none !text-white" />
	</span>
);

const StarNotePopover: React.FC<Props> = ({
	workOrderId,
	initialStarred = false,
	initialNote = '',
	noteDisplay,
	canAddNote = true,
	upsertWorkOrderStar,
	removeWorkOrderStar,
	children,
}) => {
	// Saved state from props; the note is only meaningful while the WO is starred.
	const committedNote = initialStarred ? initialNote || null : null;

	const [open, setOpen] = useState(false);
	const [draftStarred, setDraftStarred] = useState(initialStarred);
	const [draftNote, setDraftNote] = useState(committedNote || '');
	const [isPersisting, setIsPersisting] = useState(false);

	const openRef = useRef(open);
	openRef.current = open;

	// Only one floating popover (this, the label upsell, …) open at a time.
	useExclusivePopover(open, () => setOpen(false));

	useMountEffect(() => {
		const closeOnScroll = () => {
			if (openRef.current) setOpen(false);
		};
		window.addEventListener('scroll', closeOnScroll, true);
		return () => window.removeEventListener('scroll', closeOnScroll, true);
	});

	// Persist first; update UI and toasts only after the API succeeds.
	const persistStar = useCallback(
		(starred: boolean, note: string | null): Promise<boolean> => {
			setIsPersisting(true);
			const request = starred
				? upsertWorkOrderStar(workOrderId, note)
				: removeWorkOrderStar(workOrderId);
			return request
				.then(() => true)
				.catch(() => false)
				.finally(() => {
					setIsPersisting(false);
				});
		},
		[workOrderId, upsertWorkOrderStar, removeWorkOrderStar]
	);

	const showStarredToast = useCallback(() => {
		message.open({
			content: (
				<span className="work-order-starred-feedback-toast">
					<StarredToastIcon />
					<span className="work-order-starred-feedback-toast__text bodyregular text-character-primary">
						{`Tracked WO #${workOrderId}`}
					</span>
				</span>
			),
			duration: 3,
		});
	}, [workOrderId]);

	const showUnstarredToast = useCallback(
		(previousNote: string | null) => {
			const toastKey = `unstar-${workOrderId}`;
			message.open({
				key: toastKey,
				content: (
					<span className="inline-flex items-center gap-3">
						<span>{`Untracked WO #${workOrderId}`}</span>
						<Button
							size="small"
							onClick={() => {
								message.destroy(toastKey);
								persistStar(true, previousNote).then((ok) => {
									if (ok) showStarredToast();
								});
							}}
						>
							UNDO
						</Button>
					</span>
				),
				duration: 5,
			});
		},
		[workOrderId, persistStar, showStarredToast]
	);

	const openPopover = useCallback(
		(options?: { draftStarred?: boolean; draftNote?: string }) => {
			const nextDraftStarred =
				options && options.draftStarred !== undefined ? options.draftStarred : initialStarred;
			const nextDraftNote =
				options && options.draftNote !== undefined ? options.draftNote : committedNote || '';
			setDraftStarred(nextDraftStarred);
			setDraftNote(nextDraftNote);
			setOpen(true);
		},
		[initialStarred, committedNote]
	);

	const openPopoverAsUnstarDraft = useCallback(() => {
		openPopover({ draftStarred: false, draftNote: committedNote || '' });
	}, [committedNote, openPopover]);

	const handleStarClick = useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation();
			if (isPersisting) return;
			if (!initialStarred) {
				persistStar(true, null).then((ok) => {
					if (ok) showStarredToast();
				});
				return;
			}
			if (!committedNote) {
				// Starred with no note: clicking the star unstars immediately (no modal)
				// and shows the unstar toast (with UNDO). The modal is only for editing /
				// confirming when there is a note to surface.
				persistStar(false, null).then((ok) => {
					if (ok) showUnstarredToast(committedNote);
				});
				return;
			}
			openPopoverAsUnstarDraft();
		},
		[
			initialStarred,
			committedNote,
			isPersisting,
			persistStar,
			showStarredToast,
			showUnstarredToast,
			openPopoverAsUnstarDraft,
		]
	);

	const handleDropdownClick = useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation();
			if (isPersisting) return;
			if (!initialStarred) {
				// Not starred yet: open ready to star + add a note.
				openPopover({ draftStarred: true, draftNote: '' });
				return;
			}
			// Already starred: edit the note and KEEP it starred (filled star, note not
			// struck through, primary = SAVE). Only the star circle opens the unstar draft.
			openPopover();
		},
		[initialStarred, isPersisting, openPopover]
	);

	const handleSave = () => {
		if (isPersisting) return;
		const trimmedNote = draftNote.trim();
		const nextNote = draftStarred ? trimmedNote || null : null;
		persistStar(draftStarred, nextNote).then((ok) => {
			if (!ok) return;
			if (!initialStarred && draftStarred) {
				showStarredToast();
			} else if (initialStarred && !draftStarred) {
				showUnstarredToast(committedNote);
			}
			setOpen(false);
		});
	};

	const handleVisibleChange = (visible: boolean) => {
		setOpen(visible);
	};

	const StarIcon: any = draftStarred ? StarFilled : StarOutlined;

	const showUpsell = !canAddNote && !committedNote;

	const upsellContent = (
		<ProUpsellContent
			message="Pro users can track work orders with a note"
			onClose={() => setOpen(false)}
		/>
	);

	const content = (
		<div
			onClick={(event) => event.stopPropagation()}
			onMouseDown={(event) => event.stopPropagation()}
			className="relative w-[368px] pb-2xs pt-2xs"
		>
			<CloseOutlined
				onClick={() => setOpen(false)}
				className="absolute right-0 top-2xs cursor-pointer text-sm text-character-secondary"
			/>

			<div className="flex flex-col gap-m">
				<div className="pb-xs">
					<div className="flex items-center gap-0.5 pb-xs pr-[22px]">
						<span className="bodyregular whitespace-nowrap text-character-primary">
							Track Work Order with note
						</span>
						<span className="bodyregular flex-1 whitespace-pre-wrap text-character-secondary">
							{'   Only visible to you'}
						</span>
					</div>

					<div className="flex items-start gap-xs">
						<span
							onClick={() => setDraftStarred((current) => !current)}
							title={draftStarred ? 'Untrack' : 'Track'}
							className={`box-border inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full border border-solid ${
								draftStarred
									? '!border-calendulaGold-5 !bg-calendulaGold-5'
									: 'border-grey-5 bg-white'
							}`}
						>
							<StarIcon
								className={`text-xl ${draftStarred ? '!text-white' : '!text-character-title-85'}`}
							/>
						</span>
						<Input
							value={draftNote}
							disabled={!canAddNote}
							onChange={(event) => setDraftNote(event.target.value.slice(0, NOTE_MAX_LENGTH))}
							maxLength={NOTE_MAX_LENGTH}
							suffix={
								draftStarred ? (
									<span className="footnoteregular text-character-secondary">
										{`${draftNote.length}/${NOTE_MAX_LENGTH}`}
									</span>
								) : null
							}
							placeholder="Add a tracking note"
							className={
								!draftStarred && draftNote.length > 0
									? 'text-grey-7 line-through'
									: 'bodyregular text-character-primary'
							}
						/>
					</div>
				</div>

				<div className="flex justify-end gap-xs">
					<Button onClick={() => setOpen(false)} disabled={isPersisting}>
						CANCEL
					</Button>
					<Button
						type="primary"
						onClick={handleSave}
						loading={isPersisting}
						disabled={isPersisting}
					>
						{draftStarred ? 'SAVE' : 'UNTRACK'}
					</Button>
				</div>
			</div>
		</div>
	);

	const trigger = React.cloneElement(children, {
		starred: initialStarred,
		note: committedNote,
		noteDisplay,
		loading: isPersisting,
		onStarClick: handleStarClick,
		onDropdownClick: handleDropdownClick,
		onClick: undefined,
	});

	return (
		<Popover
			content={showUpsell ? upsellContent : content}
			trigger={[]}
			visible={open}
			onVisibleChange={handleVisibleChange}
			overlayClassName="!w-[400px]"
			placement="bottomLeft"
		>
			{trigger}
		</Popover>
	);
};

const mapStateToProps = (state) => ({
	userType: state.session.userType,
	currentUser: state.session.currentUser,
});
const mapDispatchToProps = (dispatch) => ({ dispatch });
const mergeProps = (stateProps, dispatchProps, ownProps: OwnProps) => {
	const { userType, currentUser } = stateProps;
	const { dispatch } = dispatchProps;
	return {
		...ownProps,
		canAddNote: canAddWOLabelsOrStarNotes(currentUser),
		upsertWorkOrderStar: (workOrderId: number | string, starNote: string | null) =>
			dispatch(upsertWorkOrderStar(userType)(workOrderId, starNote)),
		removeWorkOrderStar: (workOrderId: number | string) =>
			dispatch(removeWorkOrderStar(userType)(workOrderId)),
	};
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(StarNotePopover);
